import React, { useEffect } from "react";
import { Heading } from "@flixbus/honeycomb-react";
import { useStore } from "@/app/store";
import DelayedSpinner from "@/components/DelayedSpinner";
import useOperationalData from "../hooks/useOperationalData";
import OperationalDataPickers from "./OperationalDataPickers/OperationalDataPickers.component";
import OperationalDataGrid from "./OperationalDataGrid/OperationalDataGrid.component";
import {
  OperationalDataEmpty,
  OperationalDataError,
  OperationalDataNoYears,
  OperationalDataPrompt,
} from "./OperationalDataStates/OperationalDataStates.component";
import styles from "./OperationalData.module.scss";

const OperationalDataContainer: React.FC = () => {
  const {
    lines,
    selectedLine,
    years,
    selectedYear,
    operationalData,
    loading,
    error,
    onLineChange,
    onYearChange,
    onRetry,
  } = useOperationalData();

  // Clears on route-away unmount. The same-route param-drop case (re-clicking
  // the nav item) is cleared inside useOperationalData — both are needed.
  const clearOperationalData = useStore((s) => s.clearOperationalData);
  useEffect(() => () => clearOperationalData(), [clearOperationalData]);

  const renderBody = () => {
    if (error) return <OperationalDataError onRetry={onRetry} />;
    if (loading) return <DelayedSpinner />;
    if (operationalData) return <OperationalDataGrid data={operationalData} />;
    if (selectedLine !== null && selectedYear !== null) {
      return <OperationalDataEmpty line={selectedLine} year={selectedYear} />;
    }
    if (selectedLine !== null && years.length === 0) {
      return <OperationalDataNoYears line={selectedLine} />;
    }
    return <OperationalDataPrompt />;
  };

  return (
    <div className={styles.page}>
      <Heading size={1}>Operational Data</Heading>
      <OperationalDataPickers
        lines={lines}
        selectedLine={selectedLine}
        years={years}
        selectedYear={selectedYear}
        disabled={loading}
        onLineChange={onLineChange}
        onYearChange={onYearChange}
      />
      {renderBody()}
    </div>
  );
};

export default OperationalDataContainer;
